import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Share2, MoveRight, Pencil, Trash2, X, Folder } from 'lucide-react';
import type { Node } from '@hongweibing-netdisk/shared';
import { backdrop, scaleIn, sheetUp, spring, snappySpring } from '../lib/motion';

interface NodeActionsProps {
  node: Node;
  onClose: () => void;
  onDownload: () => void;
  onShare: () => void;
  onMove: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export function NodeActions({ node, onClose, onDownload, onShare, onMove, onRename, onDelete }: NodeActionsProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const items = [
    { icon: Download, label: '下载', onClick: node.isFolder ? undefined : onDownload, danger: false },
    { icon: Share2, label: '分享', onClick: node.isFolder ? undefined : onShare, danger: false },
    { icon: MoveRight, label: '移动到...', onClick: onMove, danger: false },
    { icon: Pencil, label: '重命名', onClick: onRename, danger: false },
    { icon: Trash2, label: '删除', onClick: onDelete, danger: true },
  ].filter((i) => i.onClick);

  return (
    <motion.div
      variants={backdrop} initial="hidden" animate="show" exit="exit"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        variants={sheetUp}
        initial="hidden" animate="show" exit="exit"
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.5 }}
        onDragEnd={(_, info) => { if (info.offset.y > 120 || info.velocity.y > 800) onClose(); }}
        style={{ touchAction: 'none', paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
        className="bg-card/95 backdrop-blur-xl border border-border-soft w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-2 sm:p-3 max-h-[80vh] overflow-y-auto soft-shadow"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 移动端拖拽把手 */}
        <div className="sm:hidden flex justify-center pt-1 pb-2 cursor-grab active:cursor-grabbing">
          <span className="w-10 h-1.5 rounded-full bg-border" />
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 mb-1 border-b border-border-soft">
          {node.isFolder
            ? <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-warning/10 flex-shrink-0"><Folder className="w-5 h-5 text-warning" /></span>
            : <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 flex-shrink-0"><Download className="w-5 h-5 text-primary" /></span>}
          <span className="text-sm text-text truncate flex-1 font-medium">{node.name}</span>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onClose} className="p-1.5 rounded-lg hover:bg-card text-text-muted transition-colors" aria-label="关闭">
            <X className="w-5 h-5" />
          </motion.button>
        </div>
        <div className="py-1">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.button
                key={it.label}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0, transition: { ...spring, delay: 0.03 * i } }}
                whileTap={{ scale: 0.97 }}
                onClick={() => { onClose(); it.onClick!(); }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors ${it.danger ? 'text-danger hover:bg-danger/10' : 'text-text hover:bg-primary/10'}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {it.label}
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
}
